import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_ACCOUNT_TAXONOMY, SET_ACCOUNT_TAXONOMY } from '../../graphql';
import { GetAccountTaxonomyResponse, TaxonomyDimension } from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';
import { useLeaveGuard } from '../../lib/useLeaveGuard';

const PAGE_TITLE = 'Taxonomy';
const PAGE_DESCRIPTION =
  'Define the controlled classification vocabulary Prism AI uses. Each dimension (e.g. "sentiment") holds the allowed terms the model may choose from. This is the account-wide canonical set; individual data sources can narrow it.';

function pack(dims: TaxonomyDimension[]) {
  return JSON.stringify(
    dims.map((d) => ({
      key: d.key,
      terms: d.terms,
      neverDerive: !!d.neverDerive,
      isFreeform: !!d.isFreeform,
    }))
  );
}

function PageHeader() {
  return (
    <div className="flex-shrink-0 mb-6">
      <h2 className="mb-1">{PAGE_TITLE}</h2>
      <p className="text-muted-foreground">{PAGE_DESCRIPTION}</p>
    </div>
  );
}

/**
 * Account taxonomy. `embedded` is the Content settings column: the section
 * title already sits on the left, so this renders only the card.
 */
export function TaxonomySettings({ embedded = false }: { embedded?: boolean }) {
  const [dimensions, setDimensions] = useState<TaxonomyDimension[]>([]);
  const [saved, setSaved] = useState('');
  const [newDimensionKey, setNewDimensionKey] = useState<string>('');
  const [addingDim, setAddingDim] = useState(false);
  const [termDrafts, setTermDrafts] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState<string>('');
  const skipDimCommit = useRef(false);

  const { accountId, isLoading: accountsLoading } = useAccount();

  const {
    loading: taxonomyLoading,
    error,
    data,
    refetch,
  } = useQuery<GetAccountTaxonomyResponse>(GET_ACCOUNT_TAXONOMY, {
    variables: { accountId },
    skip: !accountId,
  });

  const [setAccountTaxonomy, { loading: saving }] = useMutation(SET_ACCOUNT_TAXONOMY, {
    onCompleted: () => {
      refetch();
      setSaveMessage('Taxonomy saved.');
    },
    onError: (err) => {
      console.error('Error saving taxonomy:', err);
      setSaveMessage(`Failed to save: ${err.message}`);
    },
  });

  const sortByKey = (dims: TaxonomyDimension[]) =>
    [...dims].sort((a, b) => a.key.localeCompare(b.key, undefined, { sensitivity: 'base' }));

  useEffect(() => {
    if (data?.accountTaxonomy) {
      // Clone so local edits don't mutate the Apollo cache.
      const next = sortByKey(
        data.accountTaxonomy.map((d) => ({
          key: d.key,
          terms: [...d.terms],
          neverDerive: d.neverDerive ?? false,
          isFreeform: d.isFreeform ?? false,
        }))
      );
      setDimensions(next);
      setSaved(pack(next));
    }
  }, [data]);

  const addDimension = () => {
    const key = newDimensionKey.trim();
    if (!key || dimensions.some((d) => d.key.toLowerCase() === key.toLowerCase())) return;
    setDimensions((prev) =>
      sortByKey([...prev, { key, terms: [], neverDerive: false, isFreeform: false }])
    );
    setNewDimensionKey('');
  };

  // The whole dimension and every term in it: ask, naming what goes.
  const removeDimension = (key: string) => {
    const dim = dimensions.find((d) => d.key === key);
    const n = dim?.terms.length || 0;
    if (
      !window.confirm(
        `Remove the “${key}” dimension${n ? ` and its ${n} ${n === 1 ? 'term' : 'terms'}` : ''}?` +
          '\n\nIt goes when you save. Articles keep the terms they already have.'
      )
    )
      return;
    setDimensions((prev) => prev.filter((d) => d.key !== key));
  };

  const toggleFlag = (key: string, flag: 'neverDerive' | 'isFreeform') => {
    setDimensions((prev) => prev.map((d) => (d.key === key ? { ...d, [flag]: !d[flag] } : d)));
  };

  const addTerm = (key: string) => {
    const term = (termDrafts[key] || '').trim();
    if (!term) return;
    setDimensions((prev) =>
      prev.map((d) =>
        d.key === key && !d.terms.some((t) => t.toLowerCase() === term.toLowerCase())
          ? { ...d, terms: [...d.terms, term] }
          : d
      )
    );
    setTermDrafts((prev) => ({ ...prev, [key]: '' }));
  };

  const removeTerm = (key: string, term: string) => {
    setDimensions((prev) =>
      prev.map((d) => (d.key === key ? { ...d, terms: d.terms.filter((t) => t !== term) } : d))
    );
  };

  const handleSave = async () => {
    if (!accountId) return;
    setSaveMessage('');
    // Flush any term still typed in a draft input (not yet added via Enter/Term).
    const withDrafts = dimensions.map((d) => {
      const draft = (termDrafts[d.key] || '').trim();
      if (!draft || d.terms.some((t) => t.toLowerCase() === draft.toLowerCase())) {
        return d;
      }
      return { ...d, terms: [...d.terms, draft] };
    });
    // Persist every named dimension — empty controlled vocab is valid (terms can
    // be added later; freeform/synced-only dims often have none).
    const payload = withDrafts
      .map((d) => ({
        key: d.key.trim(),
        terms: d.terms,
        neverDerive: d.neverDerive ?? false,
        isFreeform: d.isFreeform ?? false,
      }))
      .filter((d) => d.key);
    if (payload.length === 0 && dimensions.length > 0) {
      setSaveMessage('Failed to save: no valid dimension keys.');
      return;
    }
    setDimensions(withDrafts);
    setTermDrafts({});
    setNewDimensionKey('');
    setAddingDim(false);
    await setAccountTaxonomy({ variables: { accountId, dimensions: payload } });
  };

  const dirty =
    saved !== '' &&
    (pack(dimensions) !== saved ||
      newDimensionKey.trim().length > 0 ||
      Object.values(termDrafts).some((t) => t.trim()));
  useLeaveGuard(dirty, 'Your taxonomy changes aren’t saved. Leave without saving?');

  const commitDimension = () => {
    if (skipDimCommit.current) {
      skipDimCommit.current = false;
      return;
    }
    const key = newDimensionKey.trim();
    if (!key) {
      setAddingDim(false);
      return;
    }
    if (dimensions.some((d) => d.key.toLowerCase() === key.toLowerCase())) return;
    addDimension();
    setAddingDim(false);
  };

  const failed = saveMessage.startsWith('Failed');

  if (accountsLoading || taxonomyLoading) {
    return <Loader label="Loading" className="ink-set-empty" />;
  }

  if (error) {
    console.error(error);
    return <p className="ink-set-empty">Error loading taxonomy</p>;
  }

  return (
    <div>
      {embedded ? null : <PageHeader />}
      <div className="ink-set-card">
        {dimensions.length === 0 && !addingDim ? (
          <p className="ink-set-empty">No dimensions yet.</p>
        ) : null}
        {dimensions.map((dim) => {
          const marks = [
            dim.neverDerive ? 'Never derive' : '',
            dim.isFreeform ? 'Freeform' : '',
          ].filter(Boolean);
          return (
            <div className="ink-dim-row" key={dim.key}>
              <div className="ink-dim-name">
                <div className="ink-dim-key">{dim.key}</div>
                {marks.length > 0 ? <div className="ink-dim-marks">{marks.join(' · ')}</div> : null}
                {dim.terms.length === 0 && !dim.isFreeform ? (
                  // Prism AI lists only dimensions that have terms (atomization/prompts.js).
                  <div className="ink-dim-marks">No terms yet, so Prism AI skips it</div>
                ) : null}
                <div className="ink-dim-tools">
                  <button
                    type="button"
                    data-on={dim.neverDerive ? 'true' : 'false'}
                    onClick={() => toggleFlag(dim.key, 'neverDerive')}
                  >
                    Never derive
                  </button>
                  <button
                    type="button"
                    data-on={dim.isFreeform ? 'true' : 'false'}
                    onClick={() => toggleFlag(dim.key, 'isFreeform')}
                  >
                    Freeform
                  </button>
                  <button
                    type="button"
                    className="ink-dim-remove"
                    onClick={() => removeDimension(dim.key)}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="ink-term-row">
                {dim.terms.map((term) => (
                  <span className="ink-term" key={term}>
                    {term}
                    <button
                      type="button"
                      aria-label={`Remove ${term}`}
                      onClick={() => removeTerm(dim.key, term)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  className="ink-term-add"
                  aria-label={`Add a term to ${dim.key}`}
                  placeholder="+ term"
                  value={termDrafts[dim.key] || ''}
                  onChange={(e) =>
                    setTermDrafts((prev) => ({ ...prev, [dim.key]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  onBlur={() => addTerm(dim.key)}
                />
              </div>
            </div>
          );
        })}
        {addingDim ? (
          <input
            className="ink-dim-input"
            autoFocus
            aria-label="New dimension"
            placeholder="dimension name"
            value={newDimensionKey}
            onChange={(e) => setNewDimensionKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                skipDimCommit.current = true;
                setNewDimensionKey('');
                setAddingDim(false);
              }
            }}
            onBlur={commitDimension}
          />
        ) : (
          <button type="button" className="ink-set-add" onClick={() => setAddingDim(true)}>
            + Add dimension
          </button>
        )}
        {dirty || saveMessage ? (
          <div className="ink-set-save" data-error={failed ? 'true' : 'false'}>
            <span>{failed ? saveMessage : dirty ? 'Unsaved changes' : saveMessage}</span>
            {dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => void handleSave()}
                disabled={saving || !accountId}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
